import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { TaxonomyEntry } from "@shared-core/data/api-types";
import { Card } from "../app/components/primitives/card";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import { Textarea } from "../app/components/primitives/textarea";
import { Label } from "../app/components/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import {
  EntityCombobox,
  type EntityOption,
} from "../app/components/patterns/EntityCombobox";
import {
  ArrowLeft,
  Paperclip,
  X,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
import { paths } from "@shared-core/routing/routes";
import { useSmartBack } from "../app/useSmartBack";
import { CUSTOMER_ISSUE_TYPES } from "@shared-core/domain/issues";
import { currentTime } from "@shared-core/domain/current-time";

const INVESTIGATION_DEPARTMENTS = [
  "Accounts",
  "Accounts - Bucks",
  "Client Service - Plymouth",
  "Client Services",
  "Client Services - Bucks",
  "Courier",
  "Customer",
  "Despatch",
  "Despatch - Bucks",
  "Despatch - Plymouth",
  "Display - Bucks",
  "Estimating",
  "External Supplier",
  "Finishing",
  "Finishing - Bucks",
  "IT",
  "Mailing",
  "Mailing - Bucks",
  "Other",
  "Outworkers",
  "Paper Supplier",
  "Pre-Press",
  "Pre-Press - Bucks",
  "Premises",
  "Print Department - Digital",
  "Print Department - Litho",
  "Printing Digital - Bucks",
  "Production Control",
  "Production Control - Bucks",
  "Production Control - Plymouth",
  "Sales",
  "Stock",
  "Studio",
] as const;

const REFERENCE_TYPES = [
  "Job Number",
  "Sales Order Number",
  "Purchase Order Number",
  "Online Order Number",
  "Invoice Number",
  "Other",
] as const;

interface StagedFile {
  key: string;
  file: File;
  filename: string;
  sizeLabel: string;
  isImage: boolean;
}

function sizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageFile(file: File): boolean {
  return file.type.startsWith("image/");
}

export function RaiseIssuePage() {
  const navigate = useNavigate();
  const goBack = useSmartBack(paths.issues());
  const adapter = useDataAdapter();
  const [searchParams] = useSearchParams();
  const prefillCustomerId = searchParams.get("customer") ?? undefined;

  const [taxonomy, setTaxonomy] = useState<TaxonomyEntry[]>([]);
  const [type, setType] = useState<string>("");
  const [customer, setCustomer] = useState<EntityOption | null>(null);
  const [identifiedBy, setIdentifiedBy] = useState<EntityOption | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [initialAction, setInitialAction] = useState("");
  const [referenceType, setReferenceType] = useState("");
  const [referenceCode, setReferenceCode] = useState("");
  const [selectedJob, setSelectedJob] = useState<EntityOption | null>(null);
  const [initialInvestigationDepartment, setInitialInvestigationDepartment] =
    useState("");
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isCustomerType = type !== "" && CUSTOMER_ISSUE_TYPES.has(type);

  // Load taxonomy on mount.
  useEffect(() => {
    adapter
      .getIssueTaxonomy()
      .then(setTaxonomy)
      .catch(() => {});
  }, [adapter]);

  const fetchCustomers = useMemo(
    () =>
      async (query: string): Promise<EntityOption[]> => {
        const resp = await adapter.getCustomersForIssuePicker({
          q: query || undefined,
          page: 1,
          pageSize: 25,
        });
        return resp.items.map((c) => ({ id: c.id, label: c.name, hint: c.id }));
      },
    [adapter],
  );

  const fetchUsers = useMemo(
    () =>
      async (query: string): Promise<EntityOption[]> => {
        const resp = await adapter.listUsers({
          q: query || undefined,
          page: 1,
          pageSize: 25,
        });
        return resp.items.map((u) => ({
          id: u.id,
          label: u.name,
          hint: u.email,
        }));
      },
    [adapter],
  );

  const fetchJobs = useMemo(
    () =>
      async (query: string): Promise<EntityOption[]> => {
        if (!customer) return [];
        const resp = await adapter.getJobsForIssuePicker(customer.id, {
          q: query || undefined,
          page: 1,
          pageSize: 25,
        });
        return resp.items.map((j) => ({
          id: j.job_no,
          label: j.job_no,
          hint: j.title,
        }));
      },
    [adapter, customer],
  );

  // Prefill the customer when raised contextually from a customer page.
  useEffect(() => {
    if (!prefillCustomerId) return;
    adapter
      .getCustomerById(prefillCustomerId)
      .then((c) => {
        if (c)
          setCustomer({
            id: c.customer.id,
            label: c.customer.name,
            hint: c.customer.id,
          });
      })
      .catch(() => {});
  }, [adapter, prefillCustomerId]);

  // Clear the customer if the user switches to an internal issue type.
  useEffect(() => {
    if (type !== "" && !CUSTOMER_ISSUE_TYPES.has(type)) setCustomer(null);
  }, [type]);

  // Clear the selected job when the customer changes or the reference type moves away from Job Number.
  useEffect(() => {
    setSelectedJob(null);
  }, [customer, referenceType]);

  function stageFiles(files: FileList | null) {
    if (!files) return;
    const next: StagedFile[] = Array.from(files).map((f, i) => ({
      key: `staged_${currentTime().getTime()}_${i}`,
      file: f,
      filename: f.name,
      sizeLabel: sizeLabel(f.size),
      isImage: isImageFile(f),
    }));
    setStagedFiles((prev) => [...prev, ...next]);
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (type === "") next.type = "Choose an issue type";
    if (!title.trim()) next.title = "Give the issue a short title";
    if (!description.trim()) next.description = "Describe what happened";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit() {
    if (!validate() || type === "") return;
    setSubmitting(true);
    setErrors({});
    try {
      const isJobNumberRef = referenceType === "Job Number";
      const issue = await adapter.raiseIssue({
        type_id: type,
        customer_id: isCustomerType ? (customer?.id ?? null) : null,
        title: title.trim(),
        description: description.trim(),
        initial_action: initialAction.trim(),
        job_no: isJobNumberRef ? (selectedJob?.id ?? null) : null,
        related_reference:
          !isJobNumberRef && referenceType && referenceCode.trim()
            ? `${referenceType}: ${referenceCode.trim()}`
            : null,
        identified_by_id: identifiedBy?.id ?? null,
        initial_investigation_department:
          initialInvestigationDepartment || null,
      });

      // Upload any staged files now that we have an issue ID.
      await Promise.all(
        stagedFiles.map((sf) =>
          adapter.uploadIssueAttachment(issue.id, sf.file),
        ),
      );

      navigate(paths.issueDetail({ id: issue.id }));
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Could not raise the issue. Please try again.";
      setErrors({ _form: message });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Issues
        </button>
        <h1 className="mb-1">Raise an issue</h1>
        <p className="text-muted-foreground">
          Capture what happened. A manager will pick it up to investigate.
        </p>
      </div>

      {/* Type + identified by + customer */}
      <Card className="p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="issue-type">Issue type</Label>
            <Select value={type} onValueChange={(v) => setType(v)}>
              <SelectTrigger
                id="issue-type"
                className="mt-1.5 w-full"
                aria-invalid={!!errors.type}
              >
                <SelectValue placeholder="Choose a type…" />
              </SelectTrigger>
              <SelectContent>
                {taxonomy.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.type && (
              <p className="text-sm text-[color:var(--color-error)] mt-1">
                {errors.type}
              </p>
            )}
          </div>

          <div>
            <Label>Identified by</Label>
            <div className="mt-1.5">
              <EntityCombobox
                value={identifiedBy}
                onChange={setIdentifiedBy}
                fetchOptions={fetchUsers}
                placeholder="Search staff…"
                ariaLabel="Staff member who identified this issue"
                className="w-full"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Who first spotted this issue (defaults to you if left blank)
            </p>
          </div>
        </div>

        {isCustomerType ? (
          <div>
            <Label>Customer (optional)</Label>
            <div className="mt-1.5">
              <EntityCombobox
                value={customer}
                onChange={setCustomer}
                fetchOptions={fetchCustomers}
                placeholder="Search customers…"
                ariaLabel="Customer this issue relates to"
                className="w-full"
              />
            </div>
          </div>
        ) : (
          type !== "" && (
            <p className="text-sm text-muted-foreground">
              This is an internal issue — it isn't tied to a customer.
            </p>
          )
        )}

        <div>
          <Label>Related reference (optional)</Label>
          <div className="mt-1.5 flex gap-2">
            <Select value={referenceType} onValueChange={setReferenceType}>
              <SelectTrigger className="w-[220px] shrink-0">
                <SelectValue placeholder="Select type…" />
              </SelectTrigger>
              <SelectContent>
                {REFERENCE_TYPES.map((rt) => (
                  <SelectItem key={rt} value={rt}>
                    {rt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {referenceType === "Job Number" ? (
              <div className="flex-1">
                <EntityCombobox
                  value={selectedJob}
                  onChange={setSelectedJob}
                  fetchOptions={fetchJobs}
                  placeholder={
                    customer ? "Search job number…" : "Select a customer first"
                  }
                  disabled={!customer}
                />
              </div>
            ) : (
              <Input
                className="flex-1"
                value={referenceCode}
                onChange={(e) => setReferenceCode(e.target.value)}
                placeholder="Enter reference…"
                disabled={!referenceType}
              />
            )}
          </div>
          {referenceType === "Job Number" && !customer && (
            <p className="text-xs text-muted-foreground mt-1">
              Choose a customer above to search their jobs
            </p>
          )}
        </div>
      </Card>

      {/* Description */}
      <Card className="p-6 space-y-4">
        <div>
          <Label htmlFor="issue-title">Title</Label>
          <Input
            id="issue-title"
            className="mt-1.5"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="A short summary, e.g. Colour mismatch on second print run"
            aria-invalid={!!errors.title}
          />
          {errors.title && (
            <p className="text-sm text-[color:var(--color-error)] mt-1">
              {errors.title}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="issue-description">What happened?</Label>
          <Textarea
            id="issue-description"
            className="mt-1.5 min-h-28"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the problem clearly — what was wrong, where it was spotted, and the impact."
            aria-invalid={!!errors.description}
          />
          {errors.description && (
            <p className="text-sm text-[color:var(--color-error)] mt-1">
              {errors.description}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="issue-initial-action">
            Initial action taken (optional)
          </Label>
          <Textarea
            id="issue-initial-action"
            className="mt-1.5"
            value={initialAction}
            onChange={(e) => setInitialAction(e.target.value)}
            placeholder="Anything done straight away to contain the problem."
          />
        </div>

        <div>
          <Label>Initial investigation by (optional)</Label>
          <Select
            value={initialInvestigationDepartment}
            onValueChange={setInitialInvestigationDepartment}
          >
            <SelectTrigger className="mt-1.5 w-full">
              <SelectValue placeholder="Select department…" />
            </SelectTrigger>
            <SelectContent>
              {INVESTIGATION_DEPARTMENTS.map((d) => (
                <SelectItem key={d} value={d}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            Which department should handle the initial investigation
          </p>
        </div>
      </Card>

      {/* Attachments */}
      <Card className="p-6 space-y-3">
        <div>
          <Label>Attachments</Label>
          <p className="text-sm text-muted-foreground">
            Add photos of the defect or supporting documents.
          </p>
        </div>
        {stagedFiles.length > 0 && (
          <ul className="space-y-2">
            {stagedFiles.map((sf) => (
              <li
                key={sf.key}
                className="flex items-center gap-3 rounded border border-border p-2"
              >
                {sf.isImage ? (
                  <ImageIcon className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <FileText className="w-4 h-4 text-muted-foreground" />
                )}
                <span className="flex-1 text-sm truncate">{sf.filename}</span>
                <span className="text-xs text-muted-foreground">
                  {sf.sizeLabel}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setStagedFiles((prev) =>
                      prev.filter((s) => s.key !== sf.key),
                    )
                  }
                  className="text-muted-foreground hover:text-[color:var(--color-error)]"
                  aria-label={`Remove ${sf.filename}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            stageFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip className="w-4 h-4" />
          Add files
        </Button>
      </Card>

      {/* Footer */}
      {errors._form && (
        <p className="text-sm text-[color:var(--color-error)] text-right">
          {errors._form}
        </p>
      )}
      <div className="flex items-center justify-end gap-3">
        <Button variant="ghost" onClick={goBack} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Raising…" : "Raise issue"}
        </Button>
      </div>
    </div>
  );
}
